<template lang="pug">
.roleta
  header.roleta__top
    button.roleta__back(@click="$router.push('/Groups')")
      FontAwesomeIcon(:icon="['fas','arrow-left']")
      span Grupos
    .roleta__title-wrap
      p.roleta__eyebrow
        span.roleta__eyebrow-pip
        | Sorteio ao vivo
      h1.roleta__title {{ getCurrentEvent ? getCurrentEvent.name : 'Sorteio dos grupos' }}
    .roleta__audio
      button.roleta__audio-btn(@click="doToggleMute" :title="muted ? 'Ativar som' : 'Silenciar'")
        FontAwesomeIcon(:icon="['fas', muted || !volume ? 'volume-mute' : 'volume-up']")
      input.roleta__audio-range(
        type="range" min="0" max="100" step="5"
        v-model.number="volume"
        title="Volume dos efeitos"
      )
      span.roleta__audio-val {{ muted ? 'mudo' : volume + '%' }}

    .roleta__counter
      span.roleta__counter-num {{ drawnCount }}/{{ totalRobots }}
      span.roleta__counter-lbl sorteados

  .roleta__state(v-if="loading") Carregando robôs inscritos...
  .roleta__state(v-else-if="!totalRobots")
    | Nenhuma categoria pronta para o sorteio. Na tela de Grupos, ligue "Usar fase de grupos" nas categorias que vão ter grupos; cada uma também precisa de robôs aprovados suficientes.

  .roleta__stage(v-else)
    //- ================= RODA =================
    .roleta__wheel-col
      .wheel
        .wheel__pointer
        svg.wheel__svg(viewBox="0 0 520 520" :style="wheelStyle")
          circle.wheel__rim(cx="260" cy="260" r="252")
          template(v-if="remaining.length === 1")
            circle(cx="260" cy="260" r="250" :fill="colorFor(remaining[0], 0)")
            text.wheel__label-single(x="260" y="266" text-anchor="middle") {{ remaining[0].name }}
          template(v-else)
            g(v-for="(s, i) in segments" :key="s.id")
              path(:d="s.d" :fill="s.color" stroke="rgba(0,0,0,.25)" stroke-width="1")
              text.wheel__label(
                :transform="'rotate(' + s.mid + ' 260 260)'"
                x="500" y="260" text-anchor="end"
                :style="{ fontSize: labelSize + 'px' }"
              ) {{ s.short }}
          circle.wheel__hub(cx="260" cy="260" r="52")
        button.wheel__spin(
          :disabled="spinning || !remaining.length"
          @click="doSpin"
        ) {{ spinning ? '...' : (remaining.length ? 'GIRAR' : 'FIM') }}

      .roleta__winner(:class="{ 'is-on': !!lastWinner && !spinning }")
        template(v-if="lastWinner && !spinning")
          span.roleta__winner-name {{ lastWinner.name }}
          span.roleta__winner-dest
            | {{ catName(lastWinner.category_id) }} · Grupo {{ letterOf(lastWinnerGroup) }}
        template(v-else-if="spinning")
          span.roleta__winner-spinning Girando...
        template(v-else)
          span.roleta__winner-idle Gire a roleta para sortear

      .roleta__actions
        button.roleta__save(v-if="allDrawn && !saved" @click="doSave") Salvar composição
        span.roleta__ok(v-if="saved")
          FontAwesomeIcon(:icon="['fas','check-circle']")
          | Grupos salvos no sistema
        button.roleta__reset(v-if="!saved && drawnCount" @click="doReset") Reiniciar sorteio
      p.roleta__err(v-if="error") {{ error }}

    //- ================= GRUPOS =================
    .roleta__groups
      .rcat(v-for="p in plans" :key="p.category_id")
        h2.rcat__name {{ p.category_name }}
        .rcat__grid
          .rgroup(v-for="(g, gi) in assigned[p.category_id]" :key="gi")
            span.rgroup__title Grupo {{ letterOf(gi) }}
            ul.rgroup__list
              li.rgroup__item(
                v-for="r in g" :key="r.id"
                :class="{ 'is-new': lastWinner && lastWinner.id === r.id }"
              ) {{ r.name }}
              li.rgroup__empty(v-if="!g.length") —
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

const SPIN_MS = 5200
const PALETTE = [
  ['#287AED', '#1B5FBF'], // categoria 1
  ['#DD3434', '#B0231F'], // categoria 2
  ['#2E9E4F', '#1F7C3C'], // categoria 3
  ['#F9A825', '#D18B12'] // categoria 4
]

export default {
  name: 'Roleta',
  data: () => ({
    loading: true,
    plans: [], // categorias que usam fase de grupos
    remaining: [], // robôs ainda na roleta
    assigned: {}, // category_id -> array de grupos (array de robôs)
    counters: {}, // category_id -> índice do próximo grupo (ciclo simples)
    rotation: 0,
    spinning: false,
    lastWinner: null,
    lastWinnerGroup: 0,
    totalRobots: 0,
    saved: false,
    error: '',
    // áudio (efeitos gerados na hora, sem arquivo)
    volume: 60,
    muted: false
  }),
  created () {
    // preferências de som ficam no navegador de quem apresenta
    const v = parseInt(localStorage.getItem('roletaVolume'), 10)
    if (!isNaN(v)) this.volume = Math.min(100, Math.max(0, v))
    this.muted = localStorage.getItem('roletaMuted') === '1'
    // AudioContext fora do `data`: é um objeto pesado e não precisa ser reativo
    this._audio = { ctx: null, master: null }
    this.setEvents().then(() => this.load())
  },
  beforeDestroy () {
    if (this._audio && this._audio.ctx) {
      this._audio.ctx.close().catch(() => {})
      this._audio = { ctx: null, master: null }
    }
  },
  computed: {
    ...mapGetters('events', ['getCurrentEventId', 'getCurrentEvent', 'getCurrentEventArenaId']),
    drawnCount () {
      return this.totalRobots - this.remaining.length
    },
    allDrawn () {
      return this.totalRobots > 0 && this.remaining.length === 0
    },
    wheelStyle () {
      return {
        transform: `rotate(${this.rotation}deg)`,
        transition: this.spinning ? `transform ${SPIN_MS}ms cubic-bezier(.12,.72,.12,1)` : 'none'
      }
    },
    labelSize () {
      const n = this.remaining.length
      if (n <= 12) return 17
      if (n <= 20) return 14
      if (n <= 30) return 12
      return 10
    },
    segments () {
      const n = this.remaining.length
      if (n < 2) return []
      const seg = 360 / n
      return this.remaining.map((r, i) => {
        const a0 = i * seg
        const a1 = (i + 1) * seg
        const p0 = this.polar(250, a0)
        const p1 = this.polar(250, a1)
        const large = seg > 180 ? 1 : 0
        return {
          id: r.id,
          d: `M 260 260 L ${p0.x} ${p0.y} A 250 250 0 ${large} 1 ${p1.x} ${p1.y} Z`,
          color: this.colorFor(r, i),
          mid: a0 + seg / 2,
          short: this.shorten(r.name)
        }
      })
    }
  },
  methods: {
    ...mapActions('events', ['setEvents']),
    ...mapActions('fights', ['fetchGroupPlan', 'setGroupsComposition']),
    ...mapActions('robots', ['fetchRobotsList']),
    letterOf (i) {
      return String.fromCharCode(65 + i)
    },

    // —— Áudio ————————————————————————————————————————————————————————————
    // Os efeitos são SINTETIZADOS na hora (Web Audio API): nada de arquivo no
    // bundle nem requisição externa. O contexto só nasce no primeiro clique em
    // GIRAR, que é o gesto do usuário exigido pelos navegadores para tocar som.
    ensureAudio () {
      const A = window.AudioContext || window.webkitAudioContext
      if (!A) return null
      if (!this._audio.ctx) {
        const ctx = new A()
        const master = ctx.createGain()
        master.connect(ctx.destination)
        this._audio.ctx = ctx
        this._audio.master = master
        this.applyVolume()
      }
      if (this._audio.ctx.state === 'suspended') this._audio.ctx.resume()
      return this._audio.ctx
    },
    applyVolume () {
      if (!this._audio || !this._audio.master) return
      const g = this.muted ? 0 : this.volume / 100
      this._audio.master.gain.value = g * 0.6 // teto: efeitos não estouram na live
    },
    doToggleMute () {
      this.muted = !this.muted
    },
    // "toc" curto do ponteiro batendo em cada divisória
    playTick () {
      const ctx = this._audio && this._audio.ctx
      if (!ctx || this.muted || !this.volume) return
      const t = ctx.currentTime
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'square'
      osc.frequency.setValueAtTime(1400, t)
      osc.frequency.exponentialRampToValueAtTime(700, t + 0.03)
      gain.gain.setValueAtTime(0.0001, t)
      gain.gain.exponentialRampToValueAtTime(0.5, t + 0.004)
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05)
      osc.connect(gain).connect(this._audio.master)
      osc.start(t)
      osc.stop(t + 0.06)
    },
    // arpejo curto quando o sorteado é revelado
    playWin () {
      const ctx = this._audio && this._audio.ctx
      if (!ctx || this.muted || !this.volume) return
      const t0 = ctx.currentTime
      const notas = [523.25, 659.25, 783.99, 1046.5] // dó-mi-sol-dó
      notas.forEach((f, i) => {
        const t = t0 + i * 0.09
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'triangle'
        osc.frequency.setValueAtTime(f, t)
        gain.gain.setValueAtTime(0.0001, t)
        gain.gain.exponentialRampToValueAtTime(0.4, t + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.45)
        osc.connect(gain).connect(this._audio.master)
        osc.start(t)
        osc.stop(t + 0.5)
      })
    },
    // avalia a MESMA curva de easing usada no CSS, para os "tocs" caírem junto
    // com as divisórias que passam pelo ponteiro (vai rareando na desaceleração)
    easeFn (x1, y1, x2, y2) {
      const cx = 3 * x1
      const bx = 3 * (x2 - x1) - cx
      const ax = 1 - cx - bx
      const cy = 3 * y1
      const by = 3 * (y2 - y1) - cy
      const ay = 1 - cy - by
      const sampleX = t => ((ax * t + bx) * t + cx) * t
      const sampleY = t => ((ay * t + by) * t + cy) * t
      const dX = t => (3 * ax * t + 2 * bx) * t + cx
      return (x) => {
        let t = x
        for (let i = 0; i < 6; i++) {
          const dx = sampleX(t) - x
          const d = dX(t)
          if (Math.abs(dx) < 1e-4 || d === 0) break
          t -= dx / d
        }
        return sampleY(t)
      }
    },
    // dispara os "tocs" ao longo do giro, acompanhando a rotação real
    scheduleTicks (from, to, segAngle) {
      const ease = this.easeFn(0.12, 0.72, 0.12, 1)
      const t0 = performance.now()
      let ultimoIdx = Math.floor(from / segAngle)
      let ultimoSom = 0
      const passo = () => {
        const p = Math.min(1, (performance.now() - t0) / SPIN_MS)
        const ang = from + (to - from) * ease(p)
        const idx = Math.floor(ang / segAngle)
        const agora = performance.now()
        if (idx !== ultimoIdx) {
          ultimoIdx = idx
          if (agora - ultimoSom > 28) { // no começo passam dezenas por segundo
            ultimoSom = agora
            this.playTick()
          }
        }
        if (p < 1 && this.spinning) requestAnimationFrame(passo)
      }
      requestAnimationFrame(passo)
    },
    catName (catID) {
      const p = this.plans.find(x => x.category_id === catID)
      return p ? p.category_name : ''
    },
    shorten (name) {
      const s = name || ''
      return s.length > 18 ? s.slice(0, 17) + '…' : s
    },
    polar (r, angleDeg) {
      const a = (angleDeg * Math.PI) / 180
      return { x: 260 + r * Math.cos(a), y: 260 + r * Math.sin(a) }
    },
    colorFor (robot, i) {
      const idx = this.plans.findIndex(p => p.category_id === robot.category_id)
      const pair = PALETTE[(idx < 0 ? 0 : idx) % PALETTE.length]
      return pair[i % 2]
    },
    load () {
      const eventId = this.getCurrentEventId
      Promise.all([
        this.fetchGroupPlan({ event_id: eventId }),
        this.fetchRobotsList({ event_id: eventId })
      ]).then(([plans, robots]) => {
        const list = Array.isArray(robots) ? robots : []
        const byId = {}
        list.forEach(r => { byId[r.id] = r })
        // só categorias que de fato terão fase de grupos
        this.plans = (Array.isArray(plans) ? plans : []).filter(p => p.use_groups)
        const remaining = []
        const assigned = {}
        const counters = {}
        this.plans.forEach(p => {
          assigned[p.category_id] = Array.from({ length: p.groups }, () => [])
          counters[p.category_id] = 0
          p.robot_ids.forEach(id => {
            const r = byId[id]
            if (r) remaining.push({ id: r.id, name: r.name, category_id: p.category_id })
          })
        })
        this.assigned = assigned
        this.counters = counters
        this.remaining = this.shuffle(remaining)
        this.totalRobots = remaining.length
        this.loading = false
      })
    },
    shuffle (arr) {
      const a = arr.slice()
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[a[i], a[j]] = [a[j], a[i]]
      }
      return a
    },
    doSpin () {
      if (this.spinning || !this.remaining.length) return
      this.error = ''
      this.ensureAudio() // clique em GIRAR = gesto que libera o áudio
      const n = this.remaining.length
      const idx = Math.floor(Math.random() * n)
      const winner = this.remaining[idx]

      if (n === 1) {
        // roda cheia: sem giro, só resolve
        this.spinning = true
        this.playTick()
        setTimeout(() => this.settle(winner), 600)
        return
      }

      // gira até o segmento sorteado parar sob o ponteiro (topo = 270°)
      const seg = 360 / n
      const target = 270 - (idx + 0.5) * seg
      const delta = ((target - this.rotation) % 360 + 360) % 360
      const from = this.rotation
      const to = from + 6 * 360 + delta
      this.spinning = true
      this.rotation = to
      this.scheduleTicks(from, to, seg)
      setTimeout(() => this.settle(winner), SPIN_MS + 120)
    },
    // Tira o sorteado da roleta e coloca no próximo grupo da categoria dele
    // (ciclo simples: A, B, C, A, B, C...). Cada categoria tem seu contador.
    settle (winner) {
      const cat = winner.category_id
      const groups = this.assigned[cat]
      const gi = this.counters[cat] % groups.length
      groups[gi].push(winner)
      this.counters[cat] = this.counters[cat] + 1
      this.remaining = this.remaining.filter(r => r.id !== winner.id)
      this.lastWinner = winner
      this.lastWinnerGroup = gi
      this.spinning = false
      this.rotation = ((this.rotation % 360) + 360) % 360 // evita números gigantes
      this.playWin()
    },
    doReset () {
      if (!window.confirm('Reiniciar o sorteio? Os robôs voltam todos para a roleta.')) return
      this.lastWinner = null
      this.saved = false
      this.error = ''
      this.load()
    },
    doSave () {
      this.error = ''
      const calls = this.plans.map(p => this.setGroupsComposition({
        event_id: this.getCurrentEventId,
        category_id: p.category_id,
        arena_id: this.getCurrentEventArenaId,
        groups: this.assigned[p.category_id].map((g, i) => ({
          name: this.letterOf(i),
          robot_ids: g.map(r => r.id)
        }))
      }))
      Promise.all(calls).then(results => {
        const bad = results.find(r => !r || r.success === false)
        if (bad) {
          this.error = (bad && bad.error) || 'Não foi possível salvar a composição.'
          return
        }
        this.saved = true
      })
    }
  },
  watch: {
    volume (v) {
      localStorage.setItem('roletaVolume', String(v))
      if (v > 0 && this.muted) this.muted = false // mexer no slider tira do mudo
      this.applyVolume()
    },
    muted (m) {
      localStorage.setItem('roletaMuted', m ? '1' : '0')
      this.applyVolume()
    }
  }
}
</script>

<style lang="sass" scoped>
  $blue: #287AED
  $red: #DD3434
  $green: #2E9E4F
  $gold: #F9A825
  $ink: #0B0E14
  $muted: #98A2B3
  $display: 'Saira Condensed', 'Lato', sans-serif
  $mono: 'Space Mono', 'Lato', monospace

  .roleta
    min-height: calc(100vh - 64px)
    background: radial-gradient(circle at 18% 6%, #15233b 0%, $ink 55%, #05070b 100%)
    color: #fff
    padding: 0 0 48px
    *
      box-sizing: border-box

    &__top
      display: flex
      align-items: center
      gap: 20px
      flex-wrap: wrap
      max-width: 1400px
      margin: 0 auto
      padding: 22px 24px 10px

    &__back
      display: inline-flex
      align-items: center
      gap: 8px
      background: rgba(255,255,255,.08)
      border: 1px solid rgba(255,255,255,.16)
      color: rgba(255,255,255,.85)
      cursor: pointer
      font-family: $mono
      font-size: 11px
      font-weight: 700
      letter-spacing: .08em
      text-transform: uppercase
      padding: 8px 14px
      border-radius: 999px
      &:hover
        background: rgba(255,255,255,.16)

    &__title-wrap
      flex: 1 1 auto
      min-width: 220px

    &__eyebrow
      display: inline-flex
      align-items: center
      gap: 10px
      font-family: $mono
      text-transform: uppercase
      letter-spacing: .22em
      font-size: 11px
      font-weight: 700
      color: rgba(255,255,255,.65)
      margin: 0 0 6px

    &__eyebrow-pip
      width: 8px
      height: 8px
      border-radius: 50%
      background: linear-gradient(135deg, #5AA0FF, $red)
      animation: rl-pulse 1.6s ease-in-out infinite

    &__title
      font-family: $display
      font-size: clamp(26px, 3.4vw, 42px)
      font-weight: 800
      text-transform: uppercase
      line-height: 1
      margin: 0

    &__audio
      display: inline-flex
      align-items: center
      gap: 10px
      padding: 6px 14px
      border-radius: 999px
      background: rgba(255,255,255,.06)
      border: 1px solid rgba(255,255,255,.14)
      &-btn
        background: none
        border: none
        cursor: pointer
        color: rgba(255,255,255,.85)
        font-size: 16px
        line-height: 1
        padding: 0
        &:hover
          color: #fff
      &-range
        width: 110px
        accent-color: $gold
        cursor: pointer
      &-val
        font-family: $mono
        font-size: 10px
        letter-spacing: .08em
        text-transform: uppercase
        color: rgba(255,255,255,.55)
        min-width: 40px
        text-align: right

    &__counter
      text-align: right
      &-num
        display: block
        font-family: $display
        font-size: 34px
        font-weight: 800
        line-height: 1
      &-lbl
        font-family: $mono
        font-size: 10px
        text-transform: uppercase
        letter-spacing: .12em
        color: rgba(255,255,255,.55)

    &__state
      max-width: 1400px
      margin: 60px auto
      padding: 0 24px
      text-align: center
      color: rgba(255,255,255,.7)
      font-size: 17px

    &__stage
      display: grid
      grid-template-columns: minmax(320px, 620px) 1fr
      gap: 28px
      align-items: start
      max-width: 1400px
      margin: 0 auto
      padding: 10px 24px

    &__wheel-col
      display: flex
      flex-direction: column
      align-items: center

    &__winner
      margin-top: 18px
      min-height: 86px
      width: 100%
      max-width: 520px
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      gap: 4px
      padding: 14px 18px
      border-radius: 16px
      border: 1px solid rgba(255,255,255,.12)
      background: rgba(255,255,255,.04)
      transition: background .25s ease, border-color .25s ease, transform .25s ease
      &.is-on
        border-color: rgba(249,168,37,.6)
        background: linear-gradient(140deg, rgba(249,168,37,.16), rgba(249,168,37,.05))
        transform: scale(1.02)
      &-name
        font-family: $display
        font-size: clamp(24px, 3vw, 38px)
        font-weight: 800
        text-transform: uppercase
        line-height: 1
        animation: rl-pop .45s cubic-bezier(.2,.9,.25,1.2) both
      &-dest
        font-family: $mono
        font-size: 12px
        letter-spacing: .1em
        text-transform: uppercase
        color: $gold
      &-spinning, &-idle
        font-family: $mono
        font-size: 12px
        letter-spacing: .16em
        text-transform: uppercase
        color: rgba(255,255,255,.5)

    &__actions
      display: flex
      align-items: center
      gap: 14px
      margin-top: 16px
      flex-wrap: wrap
      justify-content: center

    &__save
      padding: 0 26px
      height: 50px
      border: none
      border-radius: 12px
      cursor: pointer
      font-family: $display
      font-size: 17px
      font-weight: 800
      text-transform: uppercase
      color: #fff
      background: linear-gradient(140deg, $green, #1F7C3C)
      box-shadow: 0 8px 22px rgba(46,158,79,.35)
      &:hover
        filter: brightness(1.07)

    &__ok
      display: inline-flex
      align-items: center
      gap: 8px
      font-family: $display
      font-weight: 700
      font-size: 16px
      color: #5BD17F

    &__reset
      background: none
      border: 1px solid rgba(255,255,255,.2)
      color: rgba(255,255,255,.6)
      cursor: pointer
      border-radius: 999px
      padding: 8px 16px
      font-family: $mono
      font-size: 10px
      letter-spacing: .1em
      text-transform: uppercase
      &:hover
        color: #fff
        border-color: rgba(255,255,255,.45)

    &__err
      margin-top: 12px
      color: #FF8A8A
      font-size: 14px

    // ---------- GRUPOS ----------
    &__groups
      display: flex
      flex-direction: column
      gap: 22px

  .rcat
    &__name
      font-family: $display
      font-size: 20px
      font-weight: 800
      text-transform: uppercase
      letter-spacing: .04em
      margin: 0 0 12px
      padding-bottom: 8px
      border-bottom: 1px solid rgba(255,255,255,.12)
    &__grid
      display: grid
      grid-template-columns: repeat(auto-fill, minmax(min(100%, 190px), 1fr))
      gap: 12px

  .rgroup
    background: rgba(255,255,255,.05)
    border: 1px solid rgba(255,255,255,.10)
    border-radius: 12px
    padding: 12px
    &__title
      display: block
      font-family: $mono
      font-size: 10px
      font-weight: 700
      letter-spacing: .14em
      text-transform: uppercase
      color: rgba(255,255,255,.55)
      margin-bottom: 8px
    &__list
      list-style: none
      margin: 0
      padding: 0
      display: flex
      flex-direction: column
      gap: 6px
    &__item
      font-family: $display
      font-weight: 600
      font-size: 15px
      padding: 6px 10px
      border-radius: 8px
      background: rgba(255,255,255,.06)
      &.is-new
        background: linear-gradient(140deg, rgba(249,168,37,.3), rgba(249,168,37,.12))
        animation: rl-pop .5s cubic-bezier(.2,.9,.25,1.2) both
    &__empty
      color: rgba(255,255,255,.25)
      font-family: $mono
      font-size: 12px
      padding: 6px 10px

  // ---------- RODA ----------
  .wheel
    position: relative
    width: 100%
    max-width: 560px
    aspect-ratio: 1 / 1

    &__svg
      width: 100%
      height: 100%
      display: block
      filter: drop-shadow(0 18px 46px rgba(0,0,0,.55))

    &__rim
      fill: none
      stroke: rgba(255,255,255,.22)
      stroke-width: 6

    &__hub
      fill: #0B0E14
      stroke: rgba(255,255,255,.25)
      stroke-width: 3

    &__label
      fill: #fff
      font-family: 'Saira Condensed', 'Lato', sans-serif
      font-weight: 700
      dominant-baseline: middle
      paint-order: stroke
      stroke: rgba(0,0,0,.45)
      stroke-width: 3px

    &__label-single
      fill: #fff
      font-family: 'Saira Condensed', 'Lato', sans-serif
      font-weight: 800
      font-size: 30px

    // ponteiro fixo no topo
    &__pointer
      position: absolute
      top: -6px
      left: 50%
      transform: translateX(-50%)
      width: 0
      height: 0
      border-left: 17px solid transparent
      border-right: 17px solid transparent
      border-top: 34px solid $gold
      filter: drop-shadow(0 3px 8px rgba(0,0,0,.6))
      z-index: 3

    &__spin
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)
      width: 96px
      height: 96px
      border-radius: 50%
      border: 3px solid rgba(255,255,255,.3)
      background: radial-gradient(circle at 35% 30%, #2b3340, #0B0E14)
      color: #fff
      font-family: $display
      font-weight: 800
      font-size: 19px
      letter-spacing: .06em
      cursor: pointer
      z-index: 2
      transition: transform .15s ease, box-shadow .2s ease
      &:hover:not(:disabled)
        transform: translate(-50%, -50%) scale(1.06)
        box-shadow: 0 0 0 8px rgba(249,168,37,.15)
      &:disabled
        cursor: default
        color: rgba(255,255,255,.45)

  @keyframes rl-pop
    from
      opacity: 0
      transform: scale(.82)
    to
      opacity: 1
      transform: scale(1)

  @keyframes rl-pulse
    0%
      box-shadow: 0 0 0 0 rgba(90,160,255,.5)
    70%
      box-shadow: 0 0 0 8px rgba(90,160,255,0)
    100%
      box-shadow: 0 0 0 0 rgba(90,160,255,0)

  @media (max-width: 1000px)
    .roleta__stage
      grid-template-columns: 1fr
</style>
